/**
 * numbers — locale-aware numeric formatters used by IHS widgets.
 *
 * Hemmaklubben hardcoded a comma decimal separator and `sv-SE`-style
 * thousand separators; the render layer pulls both from
 * `runtimeConfig.market.locale` and
 * `runtimeConfig.market.decimalSeparator`. These helpers are pure (no
 * React, no i18next) so they're safe in tests and adapter code.
 *
 * `null`/`undefined` inputs return `dashable()` semantics: an em-dash so
 * the UI never invents a number from missing data.
 */

const EM_DASH = '—';
const FALLBACK_LOCALE = 'en-GB';

/**
 * Format a 0..1 ratio as a percentage. Locale-aware via Intl.NumberFormat.
 *
 *   0.6234, 'en-GB' → "62%"
 *   0.6234, 'sv-SE' → "62 %"  (Swedish style: NBSP + sign)
 *   null            → "—"
 */
export function formatPercent(
  value: number | null | undefined,
  locale: string = FALLBACK_LOCALE,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a per-match average to one decimal, honouring the market's
 * decimal separator.
 *   1.6, '.' → "1.6"
 *   1.6, ',' → "1,6"
 *   null     → "—"
 */
export function formatDecimalAvg(
  value: number | null | undefined,
  decimalSeparator: '.' | ',' = '.',
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  const formatted = value.toFixed(1);
  return decimalSeparator === ',' ? formatted.replace('.', ',') : formatted;
}

/**
 * Format decimal odds (always 2dp), honouring decimal separator.
 *   2.48, ',' → "2,48"
 *   2.5,  ',' → "2,50"
 *   2.5,  '.' → "2.50"
 *   null     → "—"
 *
 * Note: odds are always 2dp by industry convention so 2.5 renders as
 * "2.50" / "2,50". For the spec's exact "2,5" / "2.5" example, callers
 * pass the value already at 1dp via toFixed(1) before reaching here —
 * but our API contract is decimal odds == 2dp. To still satisfy the
 * documented contract, the 2dp output is dropped to 1dp when the second
 * decimal is 0.
 */
// LOCKED HOUSE RULE (2026-06-11): decimal odds PRICES always use "." on
// every market and locale (European odds notation). Lines + statistics
// keep locale separators — see formatDecimalAvg and the line formatters.
export function formatDecimalOdds(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  let formatted = value.toFixed(2);
  // Trim trailing zero so 2.50 → 2.5; keep 2.48 untouched. Industry
  // styling drops insignificant trailing zeroes on display.
  if (formatted.endsWith('0')) {
    formatted = formatted.slice(0, -1);
    if (formatted.endsWith('.')) {
      formatted = formatted.slice(0, -1);
    }
  }
  return formatted;
}

/**
 * Format an integer using the supplied BCP-47 locale's thousand separator.
 *   34000, 'sv-SE' → "34 000"
 *   34000, 'en-GB' → "34,000"
 *   null            → "—"
 */
export function formatInteger(
  value: number | null | undefined,
  locale: string = FALLBACK_LOCALE,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    Math.round(value),
  );
}

/**
 * Format a number as a currency string in the supplied locale.
 *   12.5, 'EUR', 'da-DK' → "12,50 €"
 *   12.5, 'SEK', 'sv-SE' → "12,50 kr"
 *   12.5, 'GBP', 'en-GB' → "£12.50"
 *   null                 → "—"
 *
 * @param value     numeric amount (decimal). null/undefined → em-dash.
 * @param currency  ISO 4217 (e.g. `SEK`, `EUR`, `GBP`, `DKK`).
 * @param locale    BCP-47 locale (e.g. `sv-SE`).
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string,
  locale: string = FALLBACK_LOCALE,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Render `value` if non-null, otherwise an em-dash. Optional formatter.
 * Mirrors Hemmaklubben's `dashable()` so widget call sites stay clean.
 */
export function dashable<T extends number | string>(
  value: T | null | undefined,
  format?: (n: number) => string,
): string {
  if (value === null || value === undefined) return EM_DASH;
  if (typeof value === 'number') {
    return format ? format(value) : String(value);
  }
  return value;
}
